import { useSuspenseQuery } from '@tanstack/react-query';
import { paths } from '@/generated/supplier-experience-api';
import apiClient from '@/lib/apiClient';

// UI Types - Flattened from SEA discriminated unions
export interface RateInformation {
  rateType: 'APR' | 'DISC' | 'REF';
  rate: number;
  spread?: number;
  referenceRate?: number;
  rateName?: string;
}

export interface RateInformations {
  marketUuid: string;
  supplierDivisionUuid: string;
  rateInformation: RateInformation;
}

// Type for SEA response discriminated union
type ApiRateInformation =
  | { rateType: 'APR'; rate: number }
  | { rateType: 'DISC'; rate: number }
  | { rateType: 'REF'; rate: number; spread: number; referenceRate: number; rateName: string }
  | { rateType: 'APR'; rate: number; spread: number; referenceRate: number; rateName: string };

/**
 * Flattens SEA discriminated union rate type to simple UI type
 */
function flattenApiRateType(apiRate: ApiRateInformation): RateInformation {
  // Handle rates with variable rate fields (REF or benchmark APR)
  if ('spread' in apiRate && 'referenceRate' in apiRate && 'rateName' in apiRate) {
    return {
      rateType: apiRate.rateType,
      rate: apiRate.rate,
      spread: apiRate.spread,
      referenceRate: apiRate.referenceRate,
      rateName: apiRate.rateName,
    };
  }

  // Handle simple APR/DISC rates
  return {
    rateType: apiRate.rateType,
    rate: apiRate.rate,
  };
}

/**
 * Normalizes API response (singular or plural) to always return RateInformations[] array.
 * If API returns singular rateInformation, maps it to all supplierMarkets.
 * If API returns rateInformations array, uses that directly.
 */
function normalizeToRateInformations(apiResponse: {
  rateInformation?: ApiRateInformation;
  rateInformations?: Array<{ marketUuid: string; supplierDivisionUuid: string; rateInformation: ApiRateInformation }>;
  supplierMarketGroups: Array<{
    supplierMarkets: Array<{
      makerOrganizationUuid?: string;
      marketUuid: string;
      supplierDivisionUuid: string;
    }>;
  }>;
}): RateInformations[] {
  if (apiResponse.rateInformations && apiResponse.rateInformations.length > 0) {
    // Validate that every supplier market has a corresponding rate information
    const allSupplierMarkets = apiResponse.supplierMarketGroups.flatMap((group) => group.supplierMarkets);

    const missingRateInfos = allSupplierMarkets.filter((market) => {
      return !apiResponse.rateInformations!.some(
        (ri) => ri.marketUuid === market.marketUuid && ri.supplierDivisionUuid === market.supplierDivisionUuid
      );
    });

    if (missingRateInfos.length > 0) {
      const missingMarkets = missingRateInfos
        .map((m) => `division ${m.supplierDivisionUuid}, market ${m.marketUuid}`)
        .join(', ');
      throw new Error(
        `Draft offer has rateInformations array but is missing rate information for ${missingRateInfos.length} supplier market(s): ${missingMarkets}`
      );
    }

    return apiResponse.rateInformations.map((ri) => ({
      marketUuid: ri.marketUuid,
      supplierDivisionUuid: ri.supplierDivisionUuid,
      rateInformation: flattenApiRateType(ri.rateInformation),
    }));
  }

  if (apiResponse.rateInformation) {
    const flattenedRate = flattenApiRateType(apiResponse.rateInformation);
    return apiResponse.supplierMarketGroups.flatMap((group) =>
      group.supplierMarkets.map((market) => ({
        marketUuid: market.marketUuid,
        supplierDivisionUuid: market.supplierDivisionUuid,
        rateInformation: flattenedRate,
      }))
    );
  }

  // Neither field present — return empty array so the status guard in DraftOfferReview
  // can handle already-submitted drafts gracefully instead of throwing unhandled errors.
  return [];
}

type DraftOfferApiResponse = paths['/offers/draft/{uuid}']['get']['responses']['200']['content']['application/json'];
type DraftOfferApiData = DraftOfferApiResponse['data'];

export type DraftType = DraftOfferApiData['draftType'];
export const DraftType = {
  SRM_INITIATED: 'SRM_INITIATED',
  ACTIONABLE_OFFER: 'ACTIONABLE_OFFER',
  ABANDONED_CART_OFFER: 'ABANDONED_CART_OFFER',
} as const satisfies Record<string, DraftType>;

export type PricingModifiers = DraftOfferApiData['pricingModifiers'];
export type RateSourceKind = NonNullable<NonNullable<PricingModifiers>['rateSourceKind']>;
export const RateSourceKind = {
  STANDARD: 'standard',
  FIXED: 'fixed',
  PROMOTION: 'promotion',
} as const satisfies Record<string, RateSourceKind>;

export type DraftOfferType = 'PREFERRED' | 'PRICE_DISCOVERY' | 'EXPRESS_ACCEPT' | 'BENCHMARK' | 'STATIC';
export const DraftOfferType = {
  PREFERRED: 'PREFERRED',
  PRICE_DISCOVERY: 'PRICE_DISCOVERY',
  EXPRESS_ACCEPT: 'EXPRESS_ACCEPT',
  BENCHMARK: 'BENCHMARK',
  STATIC: 'STATIC',
} as const satisfies Record<string, DraftOfferType>;

export type OfferFrequency = NonNullable<DraftOfferApiData['frequency']>;
export const OfferFrequency = {
  ONE_TIME: 'one-time',
  RECURRING: 'recurring',
  UNKNOWN: 'unknown',
} as const satisfies Record<string, OfferFrequency>;

export type DraftOfferData = Awaited<ReturnType<typeof fetchDraftOfferSEA>>;

type GroupingKey = 'MAKER_ORG_CURRENCY' | 'SUPPLIER_MARKET';

async function fetchDraftOfferSEA(uuid: string, groupingKey?: GroupingKey) {
  const searchParams = groupingKey ? `?groupingKey=${groupingKey}` : '';
  const draftSource = groupingKey === 'MAKER_ORG_CURRENCY' ? 'smb' : null;
  const res = await apiClient
    .get(`api/supplier-experience/offers/draft/${uuid}${searchParams}`)
    .json<paths['/offers/draft/{uuid}']['get']['responses']['200']['content']['application/json']>();

  // Normalize API response to always return rateInformations array
  // For backwards compatibility with drafts without rateInformations
  const rateInformations = normalizeToRateInformations({
    rateInformation: res.data.rateInformation as ApiRateInformation | undefined,
    rateInformations: res.data.rateInformations as
      | Array<{ marketUuid: string; supplierDivisionUuid: string; rateInformation: ApiRateInformation }>
      | undefined,
    supplierMarketGroups: res.data.supplierMarketGroups,
  });

  const spread =
    !!res.data.rateInformation && 'spread' in res.data.rateInformation ? res.data.rateInformation.spread ?? null : null;

  let type: string | undefined;
  switch (res.data.offerType) {
    case 'preferred-term':
      type = 'PREFERRED';
      break;
    case 'price-discovery':
      type = 'PRICE_DISCOVERY';
      break;
    case 'express-accept':
      type = 'EXPRESS_ACCEPT';
      break;
    case 'benchmark':
      type = 'BENCHMARK';
      break;
    case 'static':
      type = 'STATIC';
      break;
    default:
      type = undefined;
  }

  return {
    draftType: res.data.draftType,
    expirationDate: res.data.expirationDate,
    rate: res.data.rateInformation?.rate,
    rateType: res.data.rateInformation?.rateType,
    spread,
    status: res.data.status,
    submitterAuthServiceUuid: res.data.submitterUuid,
    submitterSystemName: res.data.submitterSystemName,
    supplierMarkets: res.data.supplierMarketGroups.map((group) => {
      return group.supplierMarkets[0];
    }),
    termExpirationDate: res.data.offerExpirationDate,
    type,
    uuid,
    authServiceUuid: res.data.authServiceUuid,
    rateInformations,
    frequency: res.data.frequency,
    pricingModifiers: res.data.pricingModifiers,
    draftSource,
  };
}

export default function useDraftOffer(uuid: string | undefined, groupingKey?: GroupingKey) {
  return useSuspenseQuery({
    queryKey: ['draft-offer', uuid, groupingKey],
    queryFn: async () => {
      if (!uuid) {
        return null;
      }

      return fetchDraftOfferSEA(uuid, groupingKey);
    },
  });
}
